import clsx from 'clsx';
import PropTypes from 'prop-types';
import {Tooltip} from 'react-tooltip';

import useUniqueId from '../../hooks/uniqId';
import {extractProps} from '../../utils/propUtils';
import InfoIcon from '../Icons/Info';
import InfoLink from '../Links/Info';

import './index.scss';



const propTypes = {
  /** If provided, info icon will be displayed with the specified text as the tooltip. */
  tooltipText: PropTypes.string,
  /** If provided, info icon will link to specified URL. */
  tooltipUrl: PropTypes.string,
  /**
   * Determines if tooltip link opes in new tab.
   * Forces link to be opened in new tab if true, in same tab if false.
   * If undefined, it will default to opening external links in new tab.
   */
  tooltipNewTab: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
  children: PropTypes.node.isRequired,
};

export default function Tag({tooltipUrl, tooltipText, tooltipNewTab = true, className, style, children,
  ...otherProps}) {
  const tooltipId = useUniqueId();

  const renderTooltip = () => {
    if (!tooltipText) {
      return '';
    }
    if (tooltipUrl) {
      return <InfoLink href={tooltipUrl} tooltip={tooltipText} newTab={tooltipNewTab} className="tag__tooltip" />;
    }
    // ReactTooltip needs to be a sibling or descendent of element with data-tip,
    // otherwise we get a rendering error in Chrome.

    return (
      <div className="tag__tooltip">
        <InfoIcon data-for={tooltipId} data-tip={tooltipText} /><span className="sr-only">{tooltipText}</span>
        <Tooltip id={tooltipId} effect="solid" />
      </div>
    );
  };

  return (
    <div className={clsx('tag', className)} style={style} {...extractProps(otherProps)}>
      <div className="tag__label">
        {children}
      </div>
      {renderTooltip()}
    </div>
  );
}
Tag.propTypes = propTypes;


/**
 * Render tag if applicable.
 *
 * @param {object} params
 * @param {object} params.tag
 * @param {object} params.obj
 * @param {string} params.dbProp
 * @param {string} params.indexProp
 * @param {boolean} [params.showHelp]
 * @param {string} [params.className]
 */
export function renderTagIfPresent({tag, obj, dbProp, indexProp, showHelp = true, className}) {
  let showTag = false;
  if (obj.tags) {
    if (obj.tags.includes(indexProp)) {
      showTag = true;
    }
  } else {
    showTag = !!obj[dbProp];
  }
  if (showTag) {
    const T = tag;
    return <T key={indexProp} showHelp={showHelp} className={className} />;
  }
  return null;
}
